import LocalSocket from './LocalSocket';

// Helper to determine if a gameId is for a local game
export const isLocalGameId = (gameId) => {
  return gameId && gameId.startsWith('local_');
};

// Helper to detect if we're working with a LocalSocket instance
export const isLocalSocket = (socket) => {
  return socket instanceof LocalSocket;
};

// Class to wrap socket functionality for both local and network games
export class GameSocketWrapper {
  constructor(socket, isLocal) {
    this.socket = socket;
    this.isLocal = isLocal;
    this.eventHandlers = {};
    this.connected = false;
    this.reconnecting = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Start with 1 second delay
    this.messageQueue = [];
    this.debug = true; // Enable verbose logging

    // Set a higher maxListeners limit to prevent warnings
    if (this.isLocal && typeof this.socket.setMaxListeners === 'function') {
      this.socket.setMaxListeners(30);
    }
    
    this.log('Socket wrapper created', { isLocal });
  }

  log(message, data = {}) {
    if (this.debug) {
      console.log(`[GameSocket] ${message}`, data);
    }
  }

  // **IMPORTANT**: This mimics Socket.io's on method
  on(event, callback) {
    if (typeof callback !== 'function') {
      console.error(`GameSocketWrapper.on: Invalid callback for event ${event}`);
      return this; // Return without registering to avoid errors
    }

    this.log(`Registering handler for event: ${event}`);

    // Store the callback in our handlers map
    if (!this.eventHandlers[event]) {
      this.eventHandlers[event] = new Map(); // Use Map instead of array
    }

    // Create a wrapper function that will be registered with the socket
    const handler = (...args) => {
      this.log(`Event fired: ${event}`, args);
      callback(...args);
    };

    // Store both the original callback and its wrapper
    if (!this.eventHandlers[event].has(callback)) {
      this.eventHandlers[event].set(callback, handler);

      try {
        if (this.isLocal) {
          this.socket.on(event, handler);
        } else {
          // For WebSocket implementation, wire up special events
          if (event === 'connect') {
            if (this.socket.readyState === WebSocket.OPEN) {
              // If already connected, call the handler immediately
              this.log('WebSocket already open, triggering connect event immediately');
              setTimeout(() => handler(), 0);
            }
          }
          // Other events are handled in the message event listener
        }
      } catch (err) {
        console.error(`Error registering event ${event}:`, err);
      }
    }

    return this;
  }

  // Remove an event handler
  off(event, callback) {
    try {
      if (!this.eventHandlers[event]) {
        return this; // Nothing to remove
      }

      this.log(`Removing handler for event: ${event}`);

      if (callback) {
        // Remove specific callback
        if (this.eventHandlers[event].has(callback)) {
          const handler = this.eventHandlers[event].get(callback);

          if (this.isLocal) {
            this.socket.removeListener(event, handler);
          }
          // For WebSocket, we just remove from our local map

          this.eventHandlers[event].delete(callback);
        }
      } else {
        // Remove all callbacks for this event
        if (this.isLocal) {
          for (const [_, handler] of this.eventHandlers[event]) {
            this.socket.removeListener(event, handler);
          }
        }

        delete this.eventHandlers[event];
      }
    } catch (err) {
      console.error(`Error removing event ${event}:`, err);
    }

    return this;
  }

  // Trigger handlers for an event
  _triggerEvent(event, ...args) {
    const handlers = this.eventHandlers[event];
    if (!handlers) return;

    this.log(`Triggering event: ${event}`, args);

    for (const [_, handler] of handlers) {
      try {
        handler(...args);
      } catch (err) {
        console.error(`Error in ${event} handler:`, err);
      }
    }
  }

  // **IMPORTANT**: This mimics Socket.io's emit method
  emit(event, data) {
    try {
      if (this.isLocal) {
        this.log(`Emitting local event: ${event}`, data);
        // For local games, use our custom emit_socket method
        this.socket.emit_socket(event, data);
      } else {
        // For WebSocket implementation
        if (this.socket.readyState !== WebSocket.OPEN) {
          if (event !== 'connect_to_game') {
            this.log(`Socket not connected, queueing event: ${event}`, data);
            this.messageQueue.push({ event, data });
          }
          return this;
        }
        
        this.log(`Emitting WebSocket event: ${event}`, data);
        const message = JSON.stringify({ type: event, ...data });
        this.socket.send(message);
      }
    } catch (err) {
      console.error(`Error emitting event ${event}:`, err);
    }

    return this;
  }

  // Setup WebSocket event handlers
  setupWebSocketEvents() {
    if (this.isLocal) return;

    this.log('Setting up WebSocket event handlers');

    // Clean up any existing listeners to prevent duplicates
    this.socket.removeEventListener('open', this._handleOpen);
    this.socket.removeEventListener('close', this._handleClose);
    this.socket.removeEventListener('error', this._handleError);
    this.socket.removeEventListener('message', this._handleMessage);

    // Create bound event handlers
    this._handleOpen = this._handleOpen.bind(this);
    this._handleClose = this._handleClose.bind(this);
    this._handleError = this._handleError.bind(this);
    this._handleMessage = this._handleMessage.bind(this);

    // Add event listeners
    this.socket.addEventListener('open', this._handleOpen);
    this.socket.addEventListener('close', this._handleClose);
    this.socket.addEventListener('error', this._handleError);
    this.socket.addEventListener('message', this._handleMessage);
  }

  _handleOpen() {
    this.log('WebSocket connected');
    this.connected = true;
    this.reconnectAttempts = 0;
    this.reconnecting = false;
    
    // Trigger the connect event handlers
    this._triggerEvent('connect');
    
    // Send any queued messages
    while (this.messageQueue.length > 0) {
      const { event, data } = this.messageQueue.shift();
      this.emit(event, data);
    }
  }

  _handleClose(event) {
    this.log(`WebSocket closed: ${event.code} - ${event.reason}`);
    this.connected = false;
    
    // Trigger the disconnect event handlers
    this._triggerEvent('disconnect', event);
    
    // Try to reconnect if not intentionally closed
    if (event.code !== 1000 && !this.reconnecting) {
      this.tryReconnect();
    }
  }

  _handleError(error) {
    console.error('WebSocket error:', error);
    this._triggerEvent('error', error);
  }

  _handleMessage(event) {
    try {
      this.log('WebSocket message received', event.data);
      const rawData = event.data;
      let data;
      
      try {
        data = JSON.parse(rawData);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
        console.log('Raw message:', rawData);
        return;
      }
      
      // Handle various message formats
      // Format 1: {event: "EVENT_NAME", data: {...}}
      if (data.event && data.data !== undefined) {
        this.log(`Parsed WebSocket event: ${data.event}`, data.data);
        this._triggerEvent(data.event, data.data);
      }
      // Format 2: Direct event data without wrapping
      else if (data.type) {
        this.log(`Received unwrapped WebSocket event: ${data.type}`, data);
        // Convert to expected format and trigger
        const eventName = data.type;
        delete data.type;
        this._triggerEvent(eventName, data);
      }
      // Unknown format
      else {
        console.warn('Received message with unexpected format:', data);
      }
    } catch (error) {
      console.error('Error handling WebSocket message:', error, event.data);
    }
  }

  // Connect the socket
  connect() {
    if (this.isLocal) {
      this.log('Connecting local socket');
      this.socket.connect();
      this.connected = true;
      return this;
    }
    
    // For WebSocket, setup event handlers and let the browser handle connection
    this.log('Setting up WebSocket connection');
    this.setupWebSocketEvents();
    return this;
  }

  // Try to reconnect in case of connection failures
  tryReconnect() {
    if (this.reconnecting || this.reconnectAttempts >= this.maxReconnectAttempts) {
      return;
    }
    
    this.reconnecting = true;
    this.reconnectAttempts++;
    
    this.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
    
    // Exponential backoff
    const delay = Math.min(this.reconnectDelay * Math.pow(1.5, this.reconnectAttempts - 1), 30000);
    
    setTimeout(() => {
      if (this.socket.readyState === WebSocket.CLOSED) {
        // Create a new socket with the same URL
        try {
          this.log(`Creating new WebSocket connection to ${this.socket.url}`);
          const newSocket = new WebSocket(this.socket.url);
          this.socket = newSocket;
          this.setupWebSocketEvents();
        } catch (error) {
          console.error('Error recreating WebSocket:', error);
          this.reconnecting = false;
          
          // Try again after a delay if still have attempts left
          if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.tryReconnect();
          }
        }
      } else {
        this.reconnecting = false;
      }
    }, delay);
  }

  // Disconnect the socket
  disconnect() {
    try {
      this.log('Disconnecting socket');
      
      // Clean up all event handlers first
      Object.keys(this.eventHandlers).forEach(event => {
        this.off(event);
      });

      // Clear the event handlers
      this.eventHandlers = {};

      // Then disconnect
      if (this.isLocal) {
        this.socket.disconnect();
      } else if (this.socket.readyState === WebSocket.OPEN) {
        this.socket.close(1000, "Normal closure");
      }
      
      this.connected = false;
    } catch (err) {
      console.error('Error disconnecting socket:', err);
    }

    return this;
  }

  // Get the underlying socket
  getSocket() {
    // This is crucial - we need to return a compatible interface
    // Return 'this' instead of the raw socket to ensure compatibility
    return this;
  }
}

// Factory function to create appropriate socket
export const createGameSocket = (gameId, user, options = {}) => {
  const isLocal = isLocalGameId(gameId);
  console.log(`Creating game socket for ${gameId}, isLocal: ${isLocal}`);

  if (isLocal) {
    // Create a local socket for bot games
    console.log('Creating local socket for bot game');
    const localSocket = new LocalSocket(gameId, user);
    const wrapper = new GameSocketWrapper(localSocket, true);
    return wrapper.connect();
  } else {
    // Create a WebSocket connection to Cloudflare
    const token = localStorage.getItem("token");
    if (!token) {
      console.error("No authentication token found");
      throw new Error("Authentication required");
    }
    
    console.log('Creating WebSocket connection to server');
    const gameServiceUrl = options.gameServiceUrl || 
                          (process.env.NODE_ENV === 'production' 
                            ? "wss://sockets.slotchess.com"
                            : "ws://localhost:8787");
    
    const wsUrl = `${gameServiceUrl}/game/${gameId}?token=${encodeURIComponent(token)}`;
    console.log(`WebSocket URL: ${wsUrl}`);
    
    try {
      const socket = new WebSocket(wsUrl);
      
      const wrapper = new GameSocketWrapper(socket, false);
      return wrapper.connect();
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      throw error;
    }
  }
};